import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useSelector, useDispatch } from 'react-redux';
import _ from 'lodash';
import useTimeout from 'use-timeout';
import { scroller } from 'react-scroll';
import vhCheck from 'vh-check';
import { buildProposalPages, getBackdrop } from '../../utils/proposal';
import { getContrastColor, isElementInViewport, preloadImage } from '../../utils/utils';
import { isLegacyProposal } from '../../utils/proposalUtils';
import EventProposalIntro from './EventProposalIntro';
import EventProposalPage from './EventProposalPage';
import EventProposalCTA from './EventProposalCTA';
import PageControls from './PageControls';
import CompanyInfo from '../core/CompanyInfo';
import Loading from '../webapp/Loading';
import IconButton from '../webapp/core/IconButton';
import { KebabIcon } from '../Icons';
import ProposalConfigSections from './ProposalConfigSections';
import useSectionHighlight from '../../hooks/useSectionHighlight';

const EventProposal = ({ eventId, isEmbedMode }) => {
  const event = useSelector((state) => state.proposalData);
  const proposalOverlays = useSelector((state) => state.proposalOverlays);

  useSectionHighlight(isEmbedMode);

  if (event.loading || !event.data) {
    return null;
  }

  // Redirect on error
  useEffect(() => {
    if (!isEmbedMode && !event.loading && (event.error || !event.data.proposalEnabled)) {
      window.location.href = '/';
    }
  }, [event]);

  useEffect(() => {
    if (!isEmbedMode) {
      return;
    }

    // Remove all links for embed mode.
    const links = document.getElementsByTagName('a');
    for (let i = 0; i < links.length; i += 1) {
      links[i].removeAttribute('href');
    }
  });

  const isLegacy = isLegacyProposal(event.data);
  const pages = useRef(buildProposalPages(event.data));
  const contrastColor = useRef(getContrastColor(event.data.micrositeAccentColor));
  const [currentPage, setCurrentPage] = useState(0);
  const [vh, setVh] = useState({});
  const [vw, setVw] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  if (!pages.current) {
    return null;
  }
  const pageRef = useRef(new Array(pages.current.length));

  useEffect(() => {
    window.scrollTo(0, 0);
    const handleResize = _.debounce(() => {
      if (window.innerWidth !== vw) {
        setVh(vhCheck());
        setVw(window.innerWidth);
      }
    }, 100);
    handleResize();
    const handleScroll = _.debounce((e) => {
      if (window.scrollY === 0) {
        setCurrentPage(0);
      } else {
        const visiblePage = pageRef.current.find((page) => {
          if (!page) {
            return false;
          }
          return isElementInViewport(page);
        });
        if (visiblePage) {
          setCurrentPage(pageRef.current.indexOf(visiblePage) + 1);
        }
      }
    }, 100);
    window.addEventListener('resize', handleResize);
    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const handlePageNav = (page) => {
    setCurrentPage(page);
    if (menuOpen) {
      setMenuOpen(false);
    }
    scroller.scrollTo(`page-${page}`, {
      duration: 1000,
      smooth: true,
      offset: vw <= 1024 && page !== pages.current.length ? -84 : -1,
    });
  };

  const [overlay, setOverlay] = useState(null);
  useEffect(() => {
    const legacyOverlay = event.data.captureConfig.overlays.find((o) => o.isEnabled);
    const firstUrl = legacyOverlay?.overlay;
    const firstFrameInterval = legacyOverlay?.frameInterval;

    if (!firstUrl) {
      setOverlay(false);
      return;
    }

    const urls = Array.isArray(firstUrl) ? firstUrl : [firstUrl];
    Promise.all(urls.map((url) => preloadImage(url))).then((data) => {
      if (!data[0].naturalWidth) {
        setOverlay(false);
      } else {
        setOverlay({
          frames: urls,
          dimensions: {
            w: data[0].naturalWidth,
            h: data[0].naturalHeight,
          },
          frameInterval: firstFrameInterval,
        });
      }
    });
  }, [event, proposalOverlays]);

  // Loading indicator
  if (!event || event.loading || event.error || (!event.data?.proposalEnabled && !isEmbedMode)) {
    return <Loading loading={true} />;
  }

  // Block render until overlay is either prepared or set to false
  if (overlay === null) {
    return null;
  }

  return (
    <article className="event-proposal">
      <header
        className={[
          'fixed-navbar',
          'fixed-navbar--top',
          'fixed-navbar--hide-on-desktop',
          currentPage === pages.current.length ? 'fixed-navbar--hidden' : null,
        ].join(' ')}
      >
        <div className="fixed-navbar__brand">
          {event.data.liveGallery.leadSection &&
            event.data.logoUrl &&
            (event.data.logoLink ? (
              <a href={event.data.logoLink}>
                <img className="fixed-navbar__logo" src={event.data.logoUrl} alt="" />
              </a>
            ) : (
              <img className="fixed-navbar__logo" src={event.data.logoUrl} alt="" />
            ))}
        </div>
        {event.data.liveGallery.companySection && (
          <>
            <div className="fixed-navbar__icons">
              <IconButton alt="Toggle menu" onClick={() => setMenuOpen(!menuOpen)}>
                <KebabIcon />
              </IconButton>
            </div>
            <div className={['fixed-navbar__menu', menuOpen ? 'fixed-navbar__menu--open' : null].join(' ')}>
              <CompanyInfo
                company={event.data.liveGallery.company}
                accentColor={event.data.micrositeAccentColor}
                contrastColor={contrastColor.current}
              />
            </div>
          </>
        )}
      </header>
      <EventProposalIntro eventId={eventId} onExploreClick={() => handlePageNav(1)} />

      {/* Conditional rendering: Legacy (v1) or New (v1.5+) */}
      {isLegacy ? (
        // Legacy Proposal System (v1)
        pages.current.map((page, index) => (
          <EventProposalPage
            key={page.id}
            index={index + 1} // EventProposalIntro is 0
            ref={(el) => (pageRef.current[index] = el)}
            {...page}
            eventId={eventId}
            vh={vh}
            vw={vw}
            overlay={overlay}
            goToNextPage={() => handlePageNav(index + 2)}
            goToLastPage={() => handlePageNav(pages.current.length)}
            contrastColor={contrastColor.current}
            isEmbedMode={isEmbedMode}
          />
        ))
      ) : (
        // New Proposal System (v1.5+)
        <ProposalConfigSections />
      )}

      <nav className="event-proposal__nav fixed-navbar">
        {event.data.liveGallery.companySection && (
          <CompanyInfo
            company={event.data.liveGallery.company}
            accentColor={event.data.micrositeAccentColor}
            contrastColor={contrastColor.current}
          />
        )}
        {isLegacy && (
          <EventProposalCTA
            accentColor={event.accentColor}
            bookingLink="#"
            onButtonClick={() => handlePageNav(currentPage + 1)}
            onBookNowClick={() => handlePageNav(pages.current.length)}
            hidden={currentPage === pages.current.length}
          />
        )}
        {isLegacy && (
          <PageControls
            canPageUp={currentPage > 0}
            onPageUp={() => handlePageNav(currentPage - 1)}
            canPageDown={currentPage < pages.current.length}
            onPageDown={() => handlePageNav(currentPage + 1)}
          />
        )}
      </nav>
    </article>
  );
};

export default EventProposal;
